import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";
import { Tooltip } from "antd";

import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

import { ButtonUI } from "../Common";
import CommonLayout from "../Common/Layout";
import MyAceEditor from "../Common/AceEditor";
import ComponentSuspense from "../Common/ComponentSuspense";
import { convertTimezone, getTabQuery, handleQueryResponse, setPrevState } from "../../util/common";
import WorkspaceClass from "../../service/workspace";
import { navDataConnection } from "../kmk/AppData";

import styles from "./DataExplorer/index.module.css";
import ResultTabs from "./DataExplorer/ResultTabs/Index";
import TreeStructure from "./TreeStructure";

const SIDE_PANEL_WIDTH = "335px";

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  PanelContainer: {
    padding: "4px",
    margin: "0",
    display: "flex",
    width: "100%",
    minWidth: "100%",
    maxWidth: "100%",
    minHeight: "250px",
    height: "calc(100vh - 76px)",
    borderRadius: "8px",
    marginLeft: "inherit",
    "& .ant-tree.ant-tree-block-node .ant-tree-list-holder-inner .ant-tree-node-content-wrapper": { flex: "none" },
    "& .ant-tree": {
      overflowY: "auto !important",
      overflowX: "hidden",
      height: "calc(100% - 42px)",
    },
    "& .reflex-element": {
      overflow: "hidden !important",
    },
  },
  queryArea: {
    width: "100% !important",
    height: "calc(100% - 55px) !important",
    border: "2px solid #e8e8e8",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
  TreeNode: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    display: "inline-block",
    flexGrow: 1,
    margin: 0,
    padding: 0,
    fontSize: "14px",
  },
  TreeIcon: {
    marginRight: "1px",
  },
  PanelTitle: {
    padding: "6px",
    fontSize: "1rem",
    fontWeight: "bold",
  },
  SidebarPanel: {
    border: "1px solid #ddcdcd",
    backgroundColor: "#fff",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
      style={{
        height: "100%",
      }}
    >
      {value === index && children}
    </div>
  );
}

// Custom tootip component for tree structure
const CustomToolTip = ({ data }) => {
  return (
    <>
      {Object.keys(data).map((key) => {
        return data[key] !== null ? (
          <p key={key} style={{ fontSize: "0.75rem", margin: "0" }}>
            {key} : {data[key]}
          </p>
        ) : (
          ""
        );
      })}
    </>
  );
};

export default function SnowflakeExplorer() {
  const [workspaceName, setWorkspaceName] = useState("");
  const [connectionName, setConnectionName] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [sidePanelOpen, setSidePanelOpen] = useState(true);

  const [queryResultState, setQueryResultState] = useState({
    id: 1,
    queryResultLoding: false,
    queryResultColumns: [],
    queryResult: [],
    queryError: "",
  });

  const [cursorPosition, setCursorPosition] = useState(0);
  const [copyText, setCopyText] = useState("");

  const [sidebarState, setSidebarState] = useState({
    treeData: [],
    tableColumns: [],
    tableMessage: () => {
      "";
    },
    expandedWorkspaces: [],
    expandedKeys: [],
    selectedKeys: [],
    loading: false,
    schemaLoading: false,
  });

  const { classes } = useStyles();
  const { Workspace_id, Stage_id } = useParams();

  const queryRef = useRef(null);
  const navigate = useNavigate();

  const getQueryResult = useCallback(async () => {
    setPrevState(setQueryResultState, { queryResultLoding: true });
    const workspace = new WorkspaceClass();
    const tabQuery = getTabQuery({ query, queryRef, cursorPosition });

    const response = await workspace.runSnowflakeQuery(Workspace_id, Stage_id, {
      query: tabQuery,
    });

    handleQueryResponse(response, setQueryResultState);
  }, [Workspace_id, Stage_id, query, cursorPosition]);

  const getDatabaseList = useCallback(async () => {
    setLoading(true);

    const workspace = new WorkspaceClass();
    const result = await workspace.getDatabaseList(Workspace_id, Stage_id);

    if (result.success && Object.keys(result.data).length) {
      setConnectionName(result.data.Name);
      setConnectionName(result.data.Name);
      setWorkspaceName(result.data.workspaceName || "");
      const schemaStructure = [];
      (result?.data?.objects || []).forEach((database) => {
        const createdOnDate = convertTimezone(database.createdOn || null);

        const structureObj = {
          title: (
            <Tooltip
              placement="right"
              color="#28231D"
              title={
                <CustomToolTip
                  data={{
                    Database: database.name.toUpperCase(),
                    Owner: database.owner,
                    "Created On": createdOnDate || "",
                  }}
                />
              }
            >
              <Typography
                className={classes.TreeNode}
                sx={{
                  maxWidth: "265px",
                  minWidth: "265px",
                }}
              >
                {database.name.toUpperCase()}
              </Typography>
            </Tooltip>
          ),
          id: database.name,
          key: `${database.name}-0`,
          hierarchicalName: database.name.toUpperCase(),
          selectable: false,
          icon: <StorageRoundedIcon fontSize="small" className={classes.TreeIcon} />,
          children: [],
        };

        schemaStructure.push(structureObj);
      });

      setPrevState(setSidebarState, { treeData: schemaStructure });
    } else if (!result.success || result.status === "fail") {
      navigate(`/my-workspaces/${Workspace_id}/${navDataConnection.link}`);
    }
    setLoading(false);
  }, [Workspace_id, Stage_id, classes.TreeNode, classes.TreeIcon, navigate]);

  const getSchemasTable = useCallback(
    async (selectedDatabase) => {
      setLoading(true);
      const workspace = new WorkspaceClass();
      const response = await workspace.getExternalSnowflakeSchemas(Workspace_id, Stage_id, {
        database: selectedDatabase.toUpperCase(),
      });
      if (response.data && Object.keys(response.data).length) {
        let schemaStructure = [];
        setSidebarState((prev) => {
          schemaStructure = [...prev.treeData];
          if (response.success) {
            // Creating Tree Strucure data for Schema Hierarchy
            if (response.data?.schema?.length) {
              response.data.schema.forEach((item) => {
                const index = schemaStructure.findIndex(
                  (obj) => obj.id?.toUpperCase() === item.database?.toUpperCase()
                );

                Object.keys(item.schemaData)
                  ?.sort()
                  ?.forEach((schema, i) => {
                    const schemaHierarchicalName = `${schemaStructure[
                      index
                    ].title.props.children.props.children.toUpperCase()}.${schema.toUpperCase()}`;
                    const schemaKey = `${item.database?.toUpperCase()}-${i + 1}`;

                    const createdOnDate = convertTimezone(item.schemaData[schema].detail?.SCHEMA_CREATED_ON || null);

                    //Second level node(s) for Schema like (Informational Schema/Project/Model)
                    schemaStructure[index].children.push({
                      title: (
                        <Tooltip
                          placement="right"
                          color="#28231D"
                          title={
                            <CustomToolTip
                              data={{
                                Schema: schema,
                                Owner: item.schemaData[schema].detail?.SCHEMA_OWNER || null,
                                "Created On": createdOnDate || "",
                              }}
                            />
                          }
                        >
                          <Typography
                            className={classes.TreeNode}
                            sx={{
                              maxWidth: "240px",
                              minWidth: "240px",
                            }}
                          >
                            {schema}
                          </Typography>
                        </Tooltip>
                      ),
                      key: schemaKey,
                      hierarchicalName: schemaHierarchicalName,
                      selectable: false,
                      icon: <HubOutlinedIcon fontSize="small" className={classes.TreeIcon} />,
                      children: [],
                    });

                    //Check for tables and view both are not exists in Schema set the title for same
                    if (
                      (item.schemaData[schema]?.["Views"] || []).length === 0 &&
                      (item.schemaData[schema]?.["Tables"] || []).length === 0
                    ) {
                      schemaStructure[index].children[i].children.push({
                        title: `No Tables or Views in this Schema`,
                        hierarchicalName: schemaHierarchicalName,
                        key: `${schemaKey}-1`,
                        selectable: false,
                      });
                    } else {
                      Object.keys(item.schemaData[schema]).forEach((table, j) => {
                        //Check for tables or view exists in Schema or not and set the title for same
                        if (table !== "detail") {
                          if (!Object.keys(item.schemaData[schema][table]).length) {
                            schemaStructure?.[index]?.children?.[i]?.children?.push({
                              title: `No ${table} in this Schema`,
                              hierarchicalName: schemaHierarchicalName,
                              key: `${schemaKey}-${j + 1}`,
                              selectable: false,
                            });
                          } else {
                            //Third level node(s) for Tables/Views
                            schemaStructure?.[index]?.children?.[i]?.children?.push({
                              title: table,
                              key: `${schemaKey}-${j + 1}`,
                              hierarchicalName: schemaHierarchicalName,
                              selectable: false,
                              // icon: <BackupTableIcon fontSize="small" />,
                              children: [],
                            });

                            Object.keys(item.schemaData?.[schema]?.[table])?.forEach((val) => {
                              const tableViewName = item.schemaData[schema][table][val]?.TABLE_NAME;

                              // Add quotes if smallcase table name to print it on explorer
                              const isQuotedName = /[^A-Z0-9_]/.test(tableViewName);
                              const objectHierarchicalName = isQuotedName
                                ? `${schemaHierarchicalName}."${tableViewName}"`
                                : `${schemaHierarchicalName}.${tableViewName}`;

                              //Fourth level node(s) for Table Names or Views Names
                              const colRelatedData =
                                item.schemaData[schema][table][val].TABLE_TYPE === "BASE TABLE"
                                  ? {
                                      Rows: item.schemaData[schema][table][val]?.ROW_COUNT,
                                      Columns: item.schemaData[schema][table][val]?.COLUMN_COUNT,
                                      Size: item.schemaData[schema][table][val]?.TABLE_SIZE || null,
                                    }
                                  : {};

                              const createdOnDate = convertTimezone(
                                item.schemaData[schema][table][val]?.CREATED_ON || null
                              );

                              schemaStructure[index].children[i].children[j].children.push({
                                key: `${schemaKey}-${j + 1}-${parseInt(val) + 1}`,
                                title: (
                                  <Tooltip
                                    placement="right"
                                    color="#28231D"
                                    title={
                                      <CustomToolTip
                                        data={{
                                          [item.schemaData[schema][table][val].TABLE_TYPE === "BASE TABLE"
                                            ? "Table"
                                            : "View"]: tableViewName,
                                          "Created On": createdOnDate || "",

                                          Owner: item.schemaData[schema][table][val]?.TABLE_OWNER || null,
                                          ...colRelatedData,
                                        }}
                                      />
                                    }
                                    schemaName={schema}
                                    objectName={tableViewName}
                                  >
                                    <Typography
                                      className={classes.TreeNode}
                                      sx={{
                                        maxWidth: "190px",
                                        minWidth: "190px",
                                      }}
                                    >
                                      {tableViewName}
                                    </Typography>
                                  </Tooltip>
                                ),
                                hierarchicalName: objectHierarchicalName,
                                icon: <BackupTableIcon fontSize="small" className={classes.TreeIcon} />,
                              });
                            });
                          }
                        }
                      });
                    }
                  });
              });
            } else {
              const index = schemaStructure.findIndex(
                (obj) => obj.id?.toUpperCase() === selectedDatabase?.toUpperCase()
              );
              schemaStructure[index].children.push({
                title: `No schema available in this database.`,
                key: `${selectedDatabase?.toUpperCase()}-1`,
                hierarchicalName: `${selectedDatabase?.toUpperCase()}`,
                selectable: false,
              });
            }
          }

          if (selectedDatabase) {
            const index = schemaStructure.findIndex((obj) => obj.id?.toUpperCase() === selectedDatabase?.toUpperCase());
            schemaStructure[index].icon = <StorageRoundedIcon fontSize="small" className={classes.TreeIcon} />;
          }

          return { ...prev, treeData: [...schemaStructure], schemaLoading: false };
        });
      }
      setLoading(false);
    },
    [Stage_id, Workspace_id, classes.TreeIcon, classes.TreeNode]
  );

  const editorOnChange = useCallback((value) => {
    setQuery(value);
  }, []);

  const editorOnLoad = useCallback((editor) => {
    editor.focus();
    editor.gotoLine(Infinity, Infinity);
  }, []);

  const handleCursorChange = useCallback(
    (selection) => {
      if (selection?.cursor?.row !== cursorPosition) {
        setCursorPosition(selection.cursor.row);
      }
    },
    [cursorPosition]
  );

  const getLeftPanelList = useCallback(
    async (expandedObject) => await getSchemasTable(expandedObject),
    [getSchemasTable]
  );

  const handleSidePanel = useCallback(() => {
    setSidePanelOpen(!sidePanelOpen);
  }, [sidePanelOpen]);

  // For adding copied text in the editor
  useEffect(() => {
    if (copyText.length > 0) {
      const cursorPosition = queryRef?.current?.editor.getCursorPosition();
      queryRef?.current?.editor.session.insert(cursorPosition, copyText);
      queryRef?.current?.editor.focus();
      setCopyText("");
    }
  }, [copyText, setCopyText]);

  useEffect(() => {
    getDatabaseList();
  }, [getDatabaseList]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    {
      link: `/my-workspaces/${Workspace_id}/${navDataConnection.link}`,
      displayName: "Data Connections",
      active: false,
    },
    { link: "#", displayName: connectionName, active: true },
  ];

  return (
    <CommonLayout title="Data Connections" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>

        <Box className={classes.PanelContainer}>
          <Box
            sx={{
              minWidth: sidePanelOpen ? SIDE_PANEL_WIDTH : "45px",
              maxWidth: sidePanelOpen ? SIDE_PANEL_WIDTH : "45px",
            }}
            className={classes.SidebarPanel}
          >
            {sidePanelOpen ? (
              <Box
                display="flex"
                flexDirection={"column"}
                sx={{ minHeight: "100%", maxHeight: "100%", overflow: "clip" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#f2f2f2",
                    mb: "5px",
                    borderTopLeftRadius: "8px",
                    p: "3px 1px",
                  }}
                >
                  <Typography className={classes.PanelTitle}>Databases</Typography>
                  <span>
                    <IconButton onClick={handleSidePanel}>
                      <KeyboardDoubleArrowLeftIcon sx={{ color: "black" }} />
                    </IconButton>
                  </span>
                </Box>
                <Box
                  sx={{
                    flexGrow: "1",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                  }}
                >
                  <TreeStructure
                    treeData={sidebarState?.treeData || []}
                    sidebarState={sidebarState}
                    setSidebarState={setSidebarState}
                    getSchemas={getLeftPanelList}
                    setCopyText={setCopyText}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <IconButton onClick={handleSidePanel}>
                  <KeyboardDoubleArrowRightIcon sx={{ color: "black" }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box sx={{ width: sidePanelOpen ? "calc(100% - 330px)" : "calc(100% - 45px)" }}>
            <ReflexContainer>
              <ReflexElement minSize={300}>
                <ReflexContainer
                  windowResizeAware
                  minSize={300}
                  orientation="horizontal"
                  style={{
                    borderTopRightRadius: "8px",
                  }}
                >
                  <ReflexElement
                    minSize={150}
                    className={styles.resizeDiv}
                    style={{
                      marginTop: "0",
                      padding: 0,
                      borderTop: "none",
                    }}
                    propagateDimensionsRate={50}
                    propagateDimensions={true}
                  >
                    <TabPanel>
                      <ComponentSuspense>
                        <MyAceEditor
                          onLoad={editorOnLoad}
                          onChange={editorOnChange}
                          fontSize={14}
                          className={classes.queryArea}
                          ref={queryRef}
                          label="SOQL"
                          value={query}
                          onCursorChange={(selection) => handleCursorChange(selection)}
                          autofocus
                        />
                      </ComponentSuspense>
                      <ReflexElement
                        windowResizeAware
                        maxSize={55}
                        size={55}
                        minSize={55}
                        propagateDimensions
                        propagateDimensionsRate={200}
                      >
                        <Box
                          style={{
                            position: "absolute",
                            bottom: "10px",
                            right: "10px",
                            backgroundColor: "white",
                            zIndex: 500,
                          }}
                        >
                          <ButtonUI
                            variant="contained"
                            color="success"
                            onClick={getQueryResult}
                            disabled={!query.trim() || queryResultState.queryResultLoding}
                          >
                            Run
                          </ButtonUI>
                        </Box>
                      </ReflexElement>
                    </TabPanel>
                  </ReflexElement>
                  <ReflexSplitter className={styles.resizeBorder} propagate={true} />
                  <ReflexElement
                    minSize={40}
                    size={200}
                    className={styles.resizeDiv}
                    style={{ borderBottomRightRadius: "8px" }}
                  >
                    <ResultTabs queryResultState={queryResultState} external={true} />
                  </ReflexElement>
                </ReflexContainer>
              </ReflexElement>
            </ReflexContainer>
          </Box>
        </Box>
      </Box>
    </CommonLayout>
  );
}
